import React from 'react';

import { observer } from 'mobx-react';

import Record from 'ui/Record/Record';

import MainHeader from 'components/MainHeader';
import { Button } from 'components/Button';
import LibraryCategoriesDrawer from 'components/LibraryCategoriesDrawer';

import { EntityModules } from 'core/constants';
import routes from 'core/routes';

import { useNavBarHeight } from 'utils/hooks/navbar';
import { useStore } from 'utils/hooks/store';
import { canAddToLibrary, canManageLibraryCategories } from 'utils/roles/permissions';

import HazardsTable from './components/HazardsTable';
import CreateHazardModal from './components/CreateHazardModal';
import { HeaderActions } from './styles';

const Hazards: React.FunctionComponent = (): React.ReactElement => {
  const navBarHeight = useNavBarHeight();
  const { session } = useStore();
  const [createOpen, setCreateOpen] = React.useState(false);
  const [categoriesOpen, setCategoriesOpen] = React.useState(false);
  const refreshRef = React.useRef<Function | null>(null);

  const onRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
  };

  const listPath = routes.dashboard.risks.hazards.toString();

  return (
    <>
      <Record
        Header={
          <MainHeader
            title="Hazard Library"
            breadcrumbs={[
              { label: 'Risks', link: routes.dashboard.risks.toString() },
              { label: 'Hazard library', link: listPath },
            ]}
            action={
              <HeaderActions>
                {canManageLibraryCategories(session.user) && (
                  <Button
                    variant="outlined"
                    size="large"
                    data-testid="hazards-categories-button"
                    onClick={() => setCategoriesOpen(true)}
                  >
                    Manage categories
                  </Button>
                )}
                {canAddToLibrary(session.user) && !session.user?.readOnly && (
                  <Button
                    variant="contained"
                    size="large"
                    data-testid="hazards-new-button"
                    onClick={() => setCreateOpen(true)}
                  >
                    Add hazard
                  </Button>
                )}
              </HeaderActions>
            }
          />
        }
        Content={(onError) => <HazardsTable onError={onError} onRefreshCallback={onRefreshCallback} />}
        navBarHeight={navBarHeight}
      />
      {createOpen && <CreateHazardModal onClose={() => setCreateOpen(false)} />}
      {categoriesOpen && (
        <LibraryCategoriesDrawer
          entityType={EntityModules.Hazards}
          onClose={(changed) => {
            setCategoriesOpen(false);
            if (changed) {
              refreshRef.current?.();
            }
          }}
        />
      )}
    </>
  );
};

export default observer(Hazards);
